import { useEffect, useRef, type ReactNode } from "react";
import { IconClose } from "./icons";

/**
 * A detail panel that slides over the page instead of sitting beside the list.
 *
 * Newsletters, Subscribers, Audience and Team each kept a 380px panel open
 * next to their list, with something always auto-selected, so the list never
 * had the width it needed: board columns squeezed to one word a line, table
 * columns clipped. Now the list takes the page, and the detail opens only when
 * something is chosen — closed with ×, Escape, or a click outside, and focus
 * goes back to where it was.
 */
export function DetailDrawer({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const latestClose = useRef(onClose);
  latestClose.current = onClose;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    closeRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) latestClose.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      before?.focus?.({ preventScroll: true });
    };
  }, []);

  return (
    <>
      <div className="nl-drawer-scrim" onClick={onClose} aria-hidden />
      <aside className="nl-drawer" role="dialog" aria-label={label}>
        <button ref={closeRef} type="button" className="nl-drawer-close" aria-label={`Close ${label.toLowerCase()}`} onClick={onClose}>
          <IconClose />
        </button>
        {children}
      </aside>
    </>
  );
}
